/**
 * The panel contracts (panels.html §3): pose, expression, shot, lighting, effect, set, balloon, mark and prop ids. A port of shared-cs
 * `Content/PanelIds.cs` (S1-CHR) for the cell editor and the validator.
 */
export const PANEL_POSES = [
  'stand',
  'ready',
  'aim',
  'run',
  'crouch',
  'kneel_hurt',
  'clutch_arm',
  'hands_up',
  'point',
  'reach_out',
  'fallen',
  'dead',
  'grab',
  'dragged',
  'lunge',
  'feed',
] as const;
/** Poses that can pair with another actor (`pairWith`). */
export const PAIR_POSES = ['grab', 'dragged', 'fallen'] as const;
/** Poses the zombies can take too. */
export const ZOMBIE_POSES = ['lunge', 'feed', 'grab', 'dead'] as const;
export const PANEL_POSE_HINTS: Record<(typeof PANEL_POSES)[number], string> = {
  stand: 'Neutral, arms down',
  ready: 'Weapon low, weight forward',
  aim: 'Weapon up at a look-at target',
  run: 'Mid-stride, leaning',
  crouch: 'Low, sneaking',
  kneel_hurt: 'One knee down, hand on the ground',
  clutch_arm: 'One hand gripping the other forearm',
  hands_up: 'Surrender, or "don\'t shoot"',
  point: 'Arm out at a target',
  reach_out: 'Pleading, offering a hand',
  fallen: 'On the floor, propped on an elbow',
  dead: "The raid's death topple, held",
  grab: 'Arms locked on another actor (pairs with dragged)',
  dragged: 'Pulled backwards, heels down',
  lunge: 'Zombie lunge',
  feed: 'Zombie hunched over a body',
};
/**
 * Stick-figure thumbnails for the pose picker (24×32 SVG path data): a stand-in until the live preview renders the real pose.
 * Each is a head circle plus a polyline body; they only need to read differently at a glance.
 */
export const POSE_THUMBS: Record<(typeof PANEL_POSES)[number], string> = {
  stand: 'M12 10 V20 M12 12 L8 18 M12 12 L16 18 M12 20 L9 30 M12 20 L15 30',
  ready: 'M12 10 L13 20 M12 12 L17 16 L21 15 M12 12 L16 17 M13 20 L9 30 M13 20 L16 30',
  aim: 'M12 10 V20 M12 12 L22 11 M12 12 L20 12 M12 20 L9 30 M12 20 L15 30',
  run: 'M13 10 L11 20 M12 12 L17 16 M12 12 L7 15 M11 20 L16 26 L14 30 M11 20 L6 26',
  crouch: 'M12 14 L12 22 M12 16 L17 20 M12 16 L8 20 M12 22 L17 26 L16 30 M12 22 L7 27 L8 30',
  kneel_hurt: 'M12 12 L13 21 M12 14 L8 22 M12 14 L17 18 M13 21 L18 25 L18 30 M13 21 L9 30',
  clutch_arm: 'M12 10 V20 M12 12 L15 17 L11 18 M12 12 L9 17 L12 18 M12 20 L9 30 M12 20 L15 30',
  hands_up: 'M12 10 V20 M12 12 L7 5 M12 12 L17 5 M12 20 L9 30 M12 20 L15 30',
  point: 'M12 10 V20 M12 12 L22 9 M12 12 L9 18 M12 20 L9 30 M12 20 L15 30',
  reach_out: 'M12 10 L13 20 M12 12 L21 15 M12 12 L19 17 M13 20 L9 30 M13 20 L16 30',
  fallen: 'M5 24 L16 26 M8 25 L7 30 M13 26 L21 30 M16 26 L22 26',
  dead: 'M4 28 H18 M8 28 L6 24 M14 28 L18 24 M18 28 L23 29',
  grab: 'M12 10 L14 20 M12 12 L21 13 M12 13 L21 15 M14 20 L10 30 M14 20 L17 30',
  dragged: 'M14 12 L9 22 M13 13 L19 9 M13 13 L18 7 M9 22 L16 30 M9 22 L13 30',
  lunge: 'M15 11 L10 20 M14 12 L22 13 M14 13 L22 16 M10 20 L5 29 M10 20 L16 30',
  feed: 'M15 18 L8 22 M14 19 L18 26 M14 19 L13 27 M8 22 L5 30 M8 22 L11 30',
};
/** Head-circle centres per thumbnail (cx, cy); radius 3. */
export const POSE_HEADS: Record<(typeof PANEL_POSES)[number], [number, number]> = {
  stand: [12, 7],
  ready: [12, 7],
  aim: [12, 7],
  run: [13, 7],
  crouch: [12, 11],
  kneel_hurt: [12, 9],
  clutch_arm: [12, 7],
  hands_up: [12, 7],
  point: [12, 7],
  reach_out: [12, 7],
  fallen: [3, 22],
  dead: [2, 26],
  grab: [12, 7],
  dragged: [15, 9],
  lunge: [16, 8],
  feed: [17, 15],
};
export const PANEL_EXPRESSIONS = [
  'neutral',
  'fear',
  'anger',
  'pain',
  'relief',
  'grim',
  'shock',
  'turned',
] as const;
export const PANEL_SHOTS = [
  'extreme_close',
  'close',
  'medium',
  'full',
  'wide',
  'ots',
  'raid_view',
] as const;
export const PANEL_ANGLES = ['eye', 'low', 'high'] as const;
export const PANEL_SIDES = ['auto', 'left', 'right'] as const;
export const PANEL_ON = ['face', 'hand_r', 'hand_l', 'arm_r', 'arm_l'] as const;
export const PANEL_PRESETS = ['day', 'dark', 'flashlight', 'emergency', 'silhouette', 'night'] as const;
export const PANEL_BACKDROPS = ['yellow', 'red'] as const;
export const PANEL_EFFECTS = ['speed_lines', 'ink_splat', 'sfx', 'burst', 'vignette'] as const;
export const PANEL_EFFECT_SIZES = ['s', 'm', 'l'] as const;
export const PANEL_SPEED_DIRS = ['radial', 'left', 'right', 'up', 'down'] as const;
export const PANEL_SETS = ['room', 'kit_room', 'homebase', 'street'] as const;
export const PANEL_BALLOONS = ['speech', 'shout', 'whisper', 'radio'] as const;
export const PANEL_SLOTS = ['auto', 'wide', 'half', 'full', 'inset'] as const;
export const PANEL_BORDERS = ['ink', 'none', 'jagged'] as const;
/** '' (none) plus shared-cs `PanelIds.Masks`. */
export const PANEL_MASKS = ['', 'scope', 'round'] as const;
/** Shots that frame a subject role. */
export const PANEL_SUBJECT_SHOTS = ['extreme_close', 'close', 'medium', 'full', 'ots'] as const;
export const PANEL_CAPTION_AT = ['top', 'bottom'] as const;
/** Condition marks (`conditions.types[].panelMark`) and beat marks: shared-cs `PanelIds.ConditionMarks` + `BeatMarks`. */
export const PANEL_CONDITION_MARKS = ['head_bandage', 'bite_wrap', 'sweat', 'sling'] as const;
export const PANEL_BEAT_MARKS = ['blood_hands', 'blood_arm', 'torn_sleeve'] as const;
export const PANEL_MARKS = [...PANEL_BEAT_MARKS, ...PANEL_CONDITION_MARKS] as const;
/** Worn armor models (`ArmorDef.wornModel`, joined with + for riot gear). */
export const PANEL_WORN_MODELS = ['vest', 'padded', 'arm_wraps', 'helmet'] as const;
export const PANEL_HEADWEAR = [
  'beanie',
  'beanie_ink',
  'cap',
  'bandana',
  'hair_short',
  'hair_bun',
] as const;
export const PANEL_SKIN_TONES = ['light', 'mid', 'dark'] as const;
export const PANEL_JACKETS = [
  'BenDayCyan',
  'VioletInk85',
  'NewsprintGrey',
  'VioletInk40',
  'BenDayCyan50',
  'BruiseViolet',
] as const;
export const PANEL_PROPS = ['flashlight', 'bandage_roll'] as const;
export const PANEL_HOLDS = ['weapon', 'none', 'prop:flashlight', 'prop:bandage_roll'] as const;
export const PANEL_FACINGS = ['camera', 'away', 'left', 'right'] as const;
export const PANEL_LOOKS = ['camera', 'down', 'away'] as const;
export const PANEL_MARK_PLACES = ['door', 'center', 'corner_dark'] as const;
export const PANEL_TIERS = ['high', 'mid', 'low'] as const;

/** panels.html §2 "Page layout": text limits (English source). */
export const PANEL_TEXT = { balloonChars: 90, captionChars: 120 } as const;
